/**
 * RotationManager Module
 * Handles drag/touch rotation with momentum and smooth animation
 */

import type { RotationState } from '../types';

export interface RotationConfig {
  frameCount: number;
  sensitivity: number; // Pixels per frame change
  enableMomentum: boolean;
  momentumDecay: number; // 0-1, how quickly momentum fades
  wrapAround: boolean; // true = 360° infinite loop, false = limited rotation
  onFrameChange: (frame: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

export class RotationManager {
  private state: RotationState;
  private config: RotationConfig;
  private element: HTMLElement | null = null;
  private animationId: number | null = null;
  private autoRotateId: number | null = null;
  private lastTime: number = 0;
  private enabled: boolean = true;

  constructor(config: RotationConfig) {
    this.config = config;
    this.state = {
      currentFrame: 0,
      isDragging: false,
      startX: 0,
      lastX: 0,
      velocity: 0,
    };

    // Bind methods for event handlers
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  /**
   * Attach event listeners to an element
   */
  attach(element: HTMLElement): void {
    this.element = element;

    // Mouse events
    element.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);

    // Touch events
    element.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);

    // Prevent context menu on long press
    element.addEventListener('contextmenu', (e) => e.preventDefault());

    // Set cursor style
    element.style.cursor = 'grab';
  }

  /**
   * Detach event listeners
   */
  detach(): void {
    if (this.element) {
      this.element.removeEventListener('mousedown', this.handleMouseDown);
      this.element.removeEventListener('touchstart', this.handleTouchStart);
      this.element.removeEventListener('contextmenu', (e) => e.preventDefault());
      this.element.style.cursor = '';
    }

    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);

    this.stopMomentum();
    this.stopAutoRotate();
    this.element = null;
  }

  /**
   * Mouse event handlers
   */
  private handleMouseDown(e: MouseEvent): void {
    e.preventDefault();
    this.startDrag(e.clientX);
    if (this.element) {
      this.element.style.cursor = 'grabbing';
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.state.isDragging) {
      e.preventDefault();
      this.updateDrag(e.clientX);
    }
  }

  private handleMouseUp(): void {
    if (this.state.isDragging) {
      this.endDrag();
      if (this.element) {
        this.element.style.cursor = 'grab';
      }
    }
  }

  /**
   * Touch event handlers
   */
  private handleTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      e.preventDefault();
      this.startDrag(e.touches[0].clientX);
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (this.state.isDragging && e.touches.length === 1) {
      e.preventDefault();
      this.updateDrag(e.touches[0].clientX);
    }
  }

  private handleTouchEnd(): void {
    if (this.state.isDragging) {
      this.endDrag();
    }
  }

  /**
   * Enable or disable rotation handling
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.stopMomentum();
      this.stopAutoRotate();
    }
  }

  /**
   * Check if rotation is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Core drag logic
   */
  private startDrag(x: number): void {
    if (!this.enabled) return;

    this.stopMomentum();
    this.stopAutoRotate();

    this.state.isDragging = true;
    this.state.startX = x;
    this.state.lastX = x;
    this.state.velocity = 0;
    this.lastTime = performance.now();

    this.config.onDragStart?.();
  }

  private updateDrag(x: number): void {
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    const deltaX = x - this.state.lastX;

    // Calculate frame change based on sensitivity
    const frameDelta = Math.round(deltaX / this.config.sensitivity);

    if (frameDelta !== 0) {
      // Calculate velocity for momentum
      if (deltaTime > 0) {
        this.state.velocity = deltaX / deltaTime;
      }

      // Update frame
      this.setFrame(this.state.currentFrame - frameDelta);
      this.state.lastX = x;
      this.lastTime = currentTime;
    }
  }

  private endDrag(): void {
    this.state.isDragging = false;
    this.config.onDragEnd?.();

    // Start momentum animation if enabled and velocity is significant
    if (this.config.enableMomentum && Math.abs(this.state.velocity) > 0.1) {
      this.startMomentum();
    }
  }

  /**
   * Momentum animation
   */
  private startMomentum(): void {
    const animate = () => {
      // Apply decay to velocity
      this.state.velocity *= this.config.momentumDecay;

      // Calculate frame change
      const frameDelta = Math.round(this.state.velocity * 16 / this.config.sensitivity);

      if (Math.abs(this.state.velocity) > 0.01 || frameDelta !== 0) {
        if (frameDelta !== 0) {
          this.setFrame(this.state.currentFrame - frameDelta);
        }
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.state.velocity = 0;
        this.animationId = null;
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  stopMomentum(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.state.velocity = 0;
  }

  /**
   * Auto-rotate functionality
   */
  startAutoRotate(speed: number = 100, direction: 1 | -1 = 1): void {
    this.stopAutoRotate();

    const rotate = () => {
      if (!this.state.isDragging) {
        const nextFrame = this.state.currentFrame + direction;

        // In limited mode, stop at boundaries
        if (!this.config.wrapAround) {
          if (nextFrame < 0 || nextFrame >= this.config.frameCount) {
            this.stopAutoRotate();
            return;
          }
        }

        this.setFrame(nextFrame);
      }
      this.autoRotateId = window.setTimeout(rotate, speed);
    };

    this.autoRotateId = window.setTimeout(rotate, speed);
  }

  stopAutoRotate(): void {
    if (this.autoRotateId !== null) {
      clearTimeout(this.autoRotateId);
      this.autoRotateId = null;
    }
  }

  isAutoRotating(): boolean {
    return this.autoRotateId !== null;
  }

  /**
   * Frame control
   */
  setFrame(frame: number): void {
    let newFrame: number;

    if (this.config.wrapAround) {
      // Wrap around for infinite 360° rotation
      newFrame = frame % this.config.frameCount;
      if (newFrame < 0) {
        newFrame += this.config.frameCount;
      }
    } else {
      // Clamp for limited rotation
      newFrame = Math.max(0, Math.min(frame, this.config.frameCount - 1));

      // Stop momentum if we hit the boundary
      if (newFrame === 0 || newFrame === this.config.frameCount - 1) {
        this.state.velocity = 0;
      }
    }

    if (newFrame !== this.state.currentFrame) {
      this.state.currentFrame = newFrame;
      this.config.onFrameChange(newFrame);
    }
  }

  getFrame(): number {
    return this.state.currentFrame;
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<RotationConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current state
   */
  getState(): Readonly<RotationState> {
    return { ...this.state };
  }

  /**
   * Check if currently dragging
   */
  isDragging(): boolean {
    return this.state.isDragging;
  }
}
